import { RRule } from 'rrule';

/**
 * Check if an RRULE produces an occurrence on a given date
 * @param rruleStr - RRULE string (e.g., "FREQ=WEEKLY;BYDAY=MO,TU,WE")
 * @param date - Date string in YYYY-MM-DD format
 * @returns true if the rule is active on the given date
 */
export function isActiveOnDate(rruleStr: string | null | undefined, date: string): boolean {
  // No recurrence rule = always active
  if (!rruleStr) return true;

  try {
    // Normalize the rrule string - strip any existing RRULE: prefix
    const normalizedRrule = rruleStr.replace(/^RRULE:/i, '');

    const [year, month, day] = date.split('-').map(Number);
    // Use UTC to avoid timezone shift issues - RRule operates in UTC by default,
    // so we need UTC boundaries to ensure "Tuesday" matches Tuesday regardless of local timezone
    const start = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
    const end = new Date(Date.UTC(year, month - 1, day, 23, 59, 59));

    // Set DTSTART to 7 days before the target date - close enough to minimize iterations
    // but far enough back to ensure weekly rules generate an occurrence on the target day.
    // Using year 2000 was causing massive lag (iterating 25+ years of occurrences!)
    const dtstart = new Date(start.getTime() - 7 * 24 * 60 * 60 * 1000);
    const dtstartStr = dtstart.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const ruleWithDtstart = `DTSTART:${dtstartStr}\nRRULE:${normalizedRrule}`;
    const rule = RRule.fromString(ruleWithDtstart);

    return rule.between(start, end, true).length > 0;
  } catch {
    // If parsing fails, assume always active
    return true;
  }
}
